import { useMapStore, useUserRolePermissionStore } from 'contexts/RootStore.context';
import { Horizontal } from 'gls';
import MultiSelectCheckbox from 'components/inputs/Select/MultiSelectCheckbox';
import { observer } from 'mobx-react';
import { style } from 'typestyle';
import { headerStyle } from 'styling/App.style';
import CategorySearchSelect from 'components/inputs/Select/CategorySearchSelect/CategorySearchSelect';
import useMap from 'views/map/hooks/useMap';
import { MapContainerRefContext } from 'views/map/utils/mapContainerRef.context';
import { useContext, useMemo } from 'react';
import { KeyValuePair } from 'utils/common';
import { SearchInput } from 'components/SearchInput/SearchInput';
import { AssetPermissions } from 'views/config/roles/RoleConfigDto';

export const FilterForm = observer(() => {

  const {
    filterForm,
    showAssetMarkers,
    assetStatuses,
    loadAssets,
    loadUsers,
    selectBeacon,
    abortController,
  } = useMapStore()
  const { getAssetTypes, getAssetOwners, canViewUsers, canViewAssets } = useUserRolePermissionStore()
  const { mapContainerRef } = useContext(MapContainerRefContext)
  const { fitMapToFilterResults } = useMap(useMapStore())

  const statusOptions: KeyValuePair[] = useMemo(() => {
    const options = assetStatuses.map(r => ({ key: r.description, value: r.asset_status_id }))
    options.push({ key: "No status set", value: "__null" })
    return options;
  }, [assetStatuses])

  const { asset_types, asset_owners } = useMemo(() => {
    return {
      asset_types: getAssetTypes(AssetPermissions.view),
      asset_owners: getAssetOwners(AssetPermissions.view)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchTermPresent = filterForm.$.searchTerm.value.length > 0; // note we hide dropdowns when there is a searchterm

  const onSearch = async (searchTerm: string, searchCategory: string) => {
    filterForm.$.searchTerm.onChange(searchTerm)
    filterForm.$.searchIn.onChange(searchCategory)
    if (searchCategory === "") {
      canViewAssets && loadAssets()
      canViewUsers && loadUsers()
      return
    }

    const items = searchCategory === "assets"
      ? await loadAssets()
      : await loadUsers()

    if (abortController.signal.aborted)
      return

    selectBeacon(null)
    if (items.length === 0)
      return `Sorry, no ${searchCategory === "assets" ? "assets" : "people"} matched ${searchTerm}`

    mapContainerRef && fitMapToFilterResults(mapContainerRef, items)
  }

  const filterAssets = () => {
    loadAssets().then(items => {
      mapContainerRef && fitMapToFilterResults(mapContainerRef, items)
    })
  }

  const onChangeShow = (values: any[]) => {
    if (values.length === 0)
      values = filterForm.$.show.value.includes("assets") ? ["users"] : ["assets"]
    filterForm.$.show.onChange(values)
  }

  return (
    <>
      <Horizontal className={headerStyle} spacing={10}>
        {canViewAssets && canViewUsers && (
          <CategorySearchSelect
            disableSearchOnTextOnly
            placeholder='search Asset/users'
            searchCategoriesList={[
              { key: "Asset Name", value: "assets" },
              { key: "Persons Name", value: "users" },
            ]}
            onSearch={onSearch}
            onInputchange={(term: string) => {
              if (term !== "") {
                filterForm.$.searchTerm.onChange(term)
              }
            }}
          />
        )}
        {canViewAssets && !canViewUsers && (
          <SearchInput
            width="350px"
            id='search-asset'
            value={filterForm.$.searchTerm.value}
            onChange={filterForm.$.searchTerm.onChange}
            onEnterSearch={(value) => {
              const SearchIn = value !== "" ? "assets" : ""
              filterForm.$.searchIn.onChange(SearchIn)
              filterForm.$.searchTerm.onChange(value)
              return onSearch(value, SearchIn)
            }}
          />
        )}
        {!canViewAssets && canViewUsers && (
          <SearchInput
            width="350px"
            id='search-user'
            value={filterForm.$.searchTerm.value}
            onChange={filterForm.$.searchTerm.onChange}
            onEnterSearch={(value) => {
              const SearchIn = value !== "" ? "users" : ""
              filterForm.$.searchIn.onChange(SearchIn)
              filterForm.$.searchTerm.onChange(value)
              return onSearch(value, SearchIn)
            }}
          />

        )}
        {canViewAssets && canViewUsers && !searchTermPresent && (
          <MultiSelectCheckbox
            id="assets-people-select"
            includeAll={false}
            isClearable={false}
            options={[
              { key: "Assets", value: "assets" },
              { key: "People", value: "users" },
            ]}
            value={filterForm.$.show.value}
            onChange={onChangeShow}
            className={filterComponentStyle}
            height='40px'
            selectedValuePrefix='Show: '
            selectedTextRender={(values) => {
              if (values.length > 1)
                return "Assets & People"
              return values[0].value === "assets" ? 'Assets' : 'People'
            }}
          />
        )}
      </Horizontal >
      {showAssetMarkers && canViewAssets && !searchTermPresent && (
        <Horizontal className={headerStyle} spacing={10}>
          <MultiSelectCheckbox
            id='asset-types-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Asset Type: '
            options={asset_types.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.assetTypes.value}
            onChange={(values) => {
              filterForm.$.assetTypes.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Asset Type: (0)'
            className={filterComponentStyle}
            height='40px'
          />
          <MultiSelectCheckbox
            id='owners-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Owners: '
            placeholder='Owners: (0)'
            options={asset_owners.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.owners.value}
            onChange={(values) => {
              filterForm.$.owners.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            className={filterComponentStyle}
            height='40px'
          />
          <MultiSelectCheckbox
            id='asset-statuses-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Status: '
            options={statusOptions}
            value={filterForm.$.assetStatuses.value}
            onChange={(values) => {
              filterForm.$.assetStatuses.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Status: (0)'
            className={filterComponentStyle}
            height='40px'
          />
        </Horizontal>
      )
      }
    </>
  );
})

const filterComponentStyle = style({
  minWidth: '160px',
  maxWidth: '300px',
})
